import { useState, useRef, useEffect, useCallback } from "react";

/**
 * Simple elapsed-time stopwatch shared across modules that need a visible
 * timer chip (Test mode already implements this inline — this hook lifts
 * that pattern so Module 8/9, which had no timer at all, can reuse it).
 *
 * Pass `resetKey` to auto-reset+restart whenever it changes (e.g. switching
 * MCQ questions or Module 9 passages); otherwise drive it manually via
 * start/stop/reset.
 */
export default function useElapsedTimer({ autoStart = false, resetKey } = {}) {
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);
  const startedAt = useRef(null);
  const ticker = useRef(null);

  const clear = () => { clearInterval(ticker.current); ticker.current = null; };

  const start = useCallback(() => {
    clear();
    startedAt.current = Date.now();
    ticker.current = setInterval(() => setElapsed(Date.now() - startedAt.current), 100);
    setRunning(true);
  }, []);

  const stop = useCallback(() => { clear(); setRunning(false); }, []);

  const reset = useCallback(() => { clear(); setElapsed(0); setRunning(false); }, []);

  useEffect(() => {
    // Always reset to 0 on a fresh resetKey (e.g. switching passages/
    // questions), whether or not this instance auto-starts.
    clear();
    setElapsed(0);
    setRunning(false);
    if (autoStart) start();
    return clear;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => clear, []);

  return { elapsed, running, start, stop, reset };
}
